'use client';
import React, { useState } from 'react';
import styled from '@benzinga/themetron';
import { Button } from '@benzinga/core-ui';
import { Input } from 'antd';
import Select, { StylesConfig } from 'react-select';

export interface LemonadeCompensationCalculatorProps {
  title: string;
  subtitle: string;
  result_title: string;
  powered_by: string;
  result_period?: string;
  result_description: string;
  button_title: string;
  charge_by: string;
  go_link: string;
  content_area?: string;
}
const selectOptions = [
  { label: 'Poor (< 567)', value: 566 },
  { label: 'Fair (567-669)', value: 568 },
  { label: 'Good (670-739)', value: 670 },
  { label: 'Excellent (> 740)', value: 740 },
];

export const LemonadeCompensationCalculator: React.FC<LemonadeCompensationCalculatorProps> = ({
  button_title,
  charge_by,
  go_link,
  powered_by,
  // result_period,
  result_description,
  result_title,
  subtitle,
  title,
  // content_area,
}) => {
  const [replacementCost, setReplacementCost] = useState(25000);
  const [squareFootage, setSquareFootage] = useState(250);
  const [creditScore, setCreditScore] = useState(selectOptions[0].value);
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [notFound, setNotFound] = useState('');

  const handleReplacementCost = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReplacementCost(Number(event.target.value));
  };
  const handleSquareFootage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSquareFootage(Number(event.target.value));
  };
  const handleCreditScore = (value?: string | number) => {
    if (value) {
      setCreditScore(Number(value));
    }
  };

  const calculateInsurance = () => {
    setNotFound('');
    setMinValue('');
    setMaxValue('');
    if (replacementCost < 89000) {
      setMinValue(charge_by === 'month' ? '$25' : '$300');
      setMaxValue(charge_by === 'month' ? ' - $40.08' : ' - $481');
    } else if (replacementCost >= 89000 && replacementCost <= 153000) {
      setMinValue(charge_by === 'month' ? '$40.08' : '$481');
      setMaxValue(charge_by === 'month' ? ' - $68.33' : ' - $820');
    } else if (replacementCost >= 153000 && replacementCost <= 438000 && squareFootage < 1998 && creditScore >= 567) {
      setMinValue(charge_by === 'month' ? '$68.33' : '$820');
      setMaxValue(charge_by === 'month' ? ' - $98' : ' - $1,176');
    } else if (replacementCost >= 153000 && replacementCost <= 438000 && squareFootage >= 1998 && creditScore >= 567) {
      setMinValue(charge_by === 'month' ? '$98' : '$1,176');
      setMaxValue(charge_by === 'month' ? ' - $136.66' : ' - $1,640');
    } else if (
      replacementCost > 438000 ||
      (replacementCost > 153000 && replacementCost < 438000 && creditScore < 567)
    ) {
      setMinValue(charge_by === 'month' ? '$136.66' : '$1,640');
      setMaxValue(' or more');
    } else {
      setMinValue('');
      setMaxValue('');
      setNotFound('Data No Found!!!');
    }
  };
  const customStyles:
    | Partial<
        StylesConfig<
          {
            label: string;
            value: number;
          },
          false
        >
      >
    | undefined = {
    control: () => ({
      border: '1px solid #adadad',
      color: '#0a0a0a',
      display: 'flex',
      fontFamily: 'Manrope,sans-serif',
      height: 39,
      justifyContent: 'space-between',
      lineHeight: 1,
    }),
    input: () => ({
      color: '#0a0a0a',
      fontFamily: 'Manrope,sans-serif',
      height: 39,
      lineHeight: 1,
    }),
    option: provided => ({
      ...provided,
      fontFamily: 'Manrope,sans-serif',
    }),
    singleValue: provided => ({
      fontFamily: 'Manrope,sans-serif',
      ...provided,
    }),
    valueContainer: () => ({
      color: '#0a0a0a',
      fontFamily: 'Manrope,sans-serif',
      height: 39,
      lineHeight: 1,
      paddingLeft: 10,
    }),
  };
  return (
    <LemonadeCompensationCalculatorContainer className="">
      <div className="calculator-wrapper health-insurance-data-form">
        <div className="health-widget-form-wrapper">
          {title && (
            <Title className="font-manrope leading-7 text-2xl font-bold">
              {title}
              {powered_by && (
                <TextSpan className="ml-2 text-xs whitespace-nowrap font-normal italic inline-block align-middle text-gray-500">
                  Powered by {powered_by}
                </TextSpan>
              )}
            </Title>
          )}
          {subtitle && <div className="mb-4">{subtitle}</div>}
          <div>
            <Form id="lemonade-calculator-form">
              <div className="form-field-wrap with-text">
                <Label>Replacement Cost*</Label>
                <TextSpan className="input-group-text font-manrope">$</TextSpan>
                <Input
                  className={!replacementCost ? `m-0 mb-0 font-manrope is-empty ` : `m-0 mb-0 font-manrope`}
                  id="lemonade-replacement-cost"
                  min={25000}
                  name="replacement_cost"
                  onChange={handleReplacementCost}
                  placeholder="Enter Replacement Cost"
                  step={1}
                  type="number"
                  value={replacementCost}
                />
                {!replacementCost && <p className="text-red-500 text-sm">Please fill the field.</p>}
              </div>
              <div className="form-field-wrap">
                <Label>Square Footage</Label>
                <Input
                  className={!squareFootage ? `mb-0 font-manrope is-empty ` : `m-0 mb-0 font-manrope`}
                  id="lemonade-square-footage"
                  min={0}
                  name="square_footage"
                  onChange={handleSquareFootage}
                  placeholder="Enter Square Footage"
                  step={1}
                  type="number"
                  value={squareFootage}
                />
                {!squareFootage && <p className="text-red-500 text-sm">Please fill the field.</p>}
              </div>
              <div className="form-field-wrap">
                <Label>Credit Score</Label>
                <Select
                  className=""
                  defaultValue={selectOptions[0]}
                  onChange={value => handleCreditScore(value?.value)}
                  options={selectOptions}
                  placeholder="Select Credit Score"
                  styles={customStyles}
                  theme={theme => ({
                    ...theme,
                    borderRadius: 0,
                    colors: {
                      ...theme.colors,
                    },
                  })}
                />
              </div>

              {squareFootage && creditScore && replacementCost && (
                <div className="form-field-wrap mt-4 text-center">
                  <Button
                    className="btn-cta w-full font-manrope"
                    id="lemonade-button-calculate"
                    name="getstarted"
                    onClick={calculateInsurance}
                    type="button"
                  >
                    Calculate
                  </Button>
                </div>
              )}

              {!notFound && minValue && maxValue && (
                <div className="font-manrope lemonade-result-wrapper m-0 mt-4 overflow-hidden p-2 rounded">
                  <div className="lemonade-result">
                    {result_title && (
                      <Title className="m-0 text-xl leading-6 font-manrope font-bold">{result_title}</Title>
                    )}
                    {powered_by && (
                      <ResultText className="m-0 text-xs leading-5 my-1 font-manrope">
                        powered by {powered_by}
                      </ResultText>
                    )}
                    <ResultText className="m-0 text-lg leading-5 font-manrope">
                      <BoldText className="leading-5">
                        <TextSpan id="lemonade-result-min">{minValue}</TextSpan>
                        <TextSpan id="lemonade-result-max">{maxValue} </TextSpan>
                        per month <TextSpan id="or-more"></TextSpan>
                      </BoldText>
                    </ResultText>
                  </div>
                  <div className="block my-2 text-center">
                    <ResultText className="text-center bg-blue-light_100 rounded text-white font-bold font-manrope p-4 py-2 block w-max m-auto hover:text-white   text-base leading-5">
                      <a
                        href={go_link ? go_link : `https://www.benzinga.com/go/lemonade-home-calculator`}
                        rel="nofollow noreferrer"
                        target="_blank"
                      >
                        <span>{button_title ? button_title : `Get a Quote`}</span>
                      </a>
                    </ResultText>
                  </div>
                  {result_description && <p className="text-base leading-5 m-0 font-manrope">{result_description}</p>}
                </div>
              )}
              {notFound && !minValue && !maxValue && (
                <div className="lemonade-result-no-found">
                  <p className="text-red-500 mt-2 text-sm">{notFound}</p>
                </div>
              )}
            </Form>
          </div>
        </div>
      </div>
    </LemonadeCompensationCalculatorContainer>
  );
};

const Title = styled.h3``;
const Form = styled.form``;
const ResultText = styled.p`
  a {
    color: white !important;
  }
`;
const BoldText = styled.strong``;

const TextSpan = styled.span`
  &.input-group-text {
    width: 14%;
    display: inline-block;
    background: #e9ecef;
    height: 39px;
    vertical-align: top;
    line-height: 37px;
    text-align: center;
    color: #495057;
    font-weight: 700;
    font-size: 22px;
    border: 1px solid #cacaca;
    border-radius: 0;
  }
`;

const Label = styled.label`
  display: block;
  margin: 0;
  font-weight: 400;
  line-height: 1.8;
  color: #0a0a0a;
  font-size: 16px;
  margin-bottom: 7px;
`;

const LemonadeCompensationCalculatorContainer = styled.div`
  [type='month'],
  [type='number'],
  [type='text'] {
    width: 100%;
    height: 2.4375rem;
    margin: 0 0 1rem;
    padding: 0.5rem;
    border: 1px solid #adadad;
    border-radius: 0;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: #0a0a0a;
    transition:
      box-shadow 0.5s,
      border-color 0.25s ease-in-out,
      -webkit-box-shadow 0.5s;
    &.is-empty {
      border-color: #ff4050;
      margin-bottom: 5px;
    }
    &:focus {
      box-shadow: unset;
    }
  }

  .health-insurance-data-form {
    max-width: 320px;
    margin: auto;
    &.calculator-wrapper {
      min-width: 320px;
    }
    input {
      &::placeholder {
        color: rgb(129 129 129);
      }
    }
    .btn-cta {
      line-height: 40px;
      border: 0;
      border-radius: 4px;
      color: #fefefe !important;
      font-family: sans-serif;
      background-color: #002d45;
      white-space: nowrap;
      display: inline-block;
      height: 40px;
      padding: 0 14px;
      font-weight: 600;
      text-transform: uppercase;
      text-decoration: none;
      box-shadow: 0 1px 3px 0 rgb(0 0 0 / 21%);
      font-size: 16px;
      text-shadow: 1px 0 0 rgb(0 0 0 / 10%);
      cursor: pointer;
      &:hover {
        box-shadow:
          0 2px 1px rgb(50 50 93 / 20%),
          0 3px 6px rgb(0 0 0 / 10%);
      }
    }
    .with-text {
      [type='number'] {
        width: 86%;
        display: inline-block;
      }
    }
    .health-widget-form-wrapper {
      background: #fff;
      border: 1px solid #ccc;
      padding: 15px;
      max-width: 100%;
      margin: 40px auto auto;
    }
    .lemonade-result-wrapper {
      background: #ffddc4;
    }
    .bg-blue-light_100 {
      --tw-bg-opacity: 1;
      background-color: #1a79ff;
    }
  }
`;
